import {
  exportAlerts,
  getActiveAlertsKpi,
  getAlertAudit,
  getAlertById,
  getAlertConfigOptions,
  getAlerts,
  getAlertsSummary,
  getAvgDaysToResolveKpi,
  getCreatedAlertsKpi,
  getCreatedFlagsKpi,
  getLinkedAlerts,
  getMonthOverMonthChart,
  resolveAlert,
  selectAlertOption,
} from './alerts';
import { getAlertsSettings, updateAlertsSettings } from './alerts-settings';
import { getAllAmenities, getAmenitiesById } from './amenities';
import { updateAmenities } from './amenities/update-amenities';
import { getOrganizationLogs } from './audit-logs';
import {
  checkSession,
  createImpersonationLink,
  forgotPassword,
  impersonateExchange,
  login,
  logout,
  refreshToken,
  resetPassword,
} from './auth';
import { getChangelog } from './changelog';
import {
  assignFacilities,
  getAdditionalCharges,
  getAlertsPerResident,
  getAverageNumberOfResidents,
  getExpenses,
  getFacilities,
  getFacilitiesByManager,
  getFacilitiesOptions,
  getFacilitiesPerformance,
  getFacilitiesPricingModels,
  getFacilityAlertsAndFlagsChart,
  getFacilityById,
  getFacilityExpenses,
  getFacilityFlags,
  getFacilityLevelsOfCare,
  getFacilityLocRatesAndRevenue,
  getFacilityOccupancy,
  getFacilityOccupancyForecast,
  getFacilityRevenue,
  getFacilityRevenueForecast,
  getFacilityTypes,
  getMargin,
  getMarginById,
  getOccupancyLosScatter,
  getOverallOccupancy,
  getOverallOccupancyForecast,
  getPayerDistribution,
  getPayerTypes,
  getResolvedAlerts,
  getRevenue,
  getRevenueAnalysis,
  getRevenueAnalysisForecast,
  getRevenueForecast,
  getRevenuePerBedScoped,
  getRevenuePerResident,
  getRevenuePerResidentScoped,
  postDashboardInsight,
  streamDashboardInsight,
  unassignFacility,
  updateFacility,
  updatePayerTypes,
} from './facilities';
import { getBedHistory, getFacilityFloorPlan } from './floor-plan';
import {
  createEmrIntegration,
  getEmrIntegrationProgress,
  getSlingConnectionStatus,
  refetchEmrIntegration,
  saveSlingConnection,
  updateEmrCredentials,
} from './integrations';
import {
  getMarketFacilities,
  getMarketFacilityVersionHistory,
} from './market-facilities';
import {
  createMarketFacilityForm,
  getMarketFacilityFormById,
  publishMarketFacilityForm,
  searchMarketFacilityForms,
  updateMarketFacilityForm,
  uploadMarketFacilityFormFile,
} from './market-facilities-form';
import {
  getAiInsight,
  getAiSummary,
  getAlertSeverityDistribution,
  getAlertsPerPerson,
  getAlertsResolutionEffectiveness,
  getAlertsTrendsTable,
  getAlertTopCategories,
  getAvgDaysToResolve,
  getComparisonInfo,
  getComparisonTable,
  getCompetitorsLocations,
  getFirstYearDischargeRate,
  getFlagsPerPerson,
  getLengthOfStayDistribution,
  getMarginKpi,
  getMarketIntelReportPdfUrl,
  getPayerDistributionChart,
  getPotentialRevenueLossKpi,
  getResidentRetention,
  getStaffTenureDistribution,
  getStaffTrendsTable,
  getTotalAdmissionsKpi,
  getTotalDischargesByType,
  getTotalDischargesKpi,
  getTrendsTable,
  simulateImpact,
} from './market-intel';
import {
  generateReportPdf,
  getSubscriptions,
  getSubscriptionStatus,
  initializeSubscription,
  updateSubscription,
} from './market-intel-subscriptions';
import { getAmrKpi } from './market-intel/get-amr-kpi';
import { getOccupancyKpi } from './market-intel/get-occupancy-kpi';
import { getStaffAverageTenure } from './market-intel/get-staff-average-tenure';
import { getStaffStaffingRatio } from './market-intel/get-staff-staffing-ratio';
import { getStaffTurnoverRate } from './market-intel/get-staff-turnover-rate';
import { createOrganizationWithUser, getOrganizations } from './organizations';
import {
  addFamilyCommunication,
  addResidentNote,
  deleteFamilyCommunication,
  deleteResidentNote,
  downloadFamilyCommunication,
  getAdmissionsDischargesChart,
  getAdmissionsKpi,
  getAlertsKpi,
  getDischargesByType,
  getDischargesKpi,
  getFamilyCommunications,
  getLengthOfStayDistributionById,
  getLengthOfStayKpi,
  getPayAcrossCareLevels,
  getResidentAlerts,
  getResidentById,
  getResidentNotes,
  getResidents,
  updateFamilyCommunication,
  updateResidentNote,
} from './residents';
import { exportResidents } from './residents/export-residents';
import {
  addPermissionToRole,
  createRole,
  getCreatableRoles,
  getRolePermissionMatrix,
  getRoles,
  removePermissionFromRole,
  updateRole,
} from './roles';
import {
  deleteNote,
  downloadStaffImportTemplate,
  exportStaff,
  getHiresTurnoversChart,
  getNewHires,
  getOverstaffed,
  getStaff,
  getStaffById,
  getStaffingRatio,
  getStaffingRatioTable,
  getTenureDistribution,
  getTotalStaff,
  getTurnovers,
  getUnderstaffed,
  getWageHistory,
  importStaff,
  updateNote,
  updateStaffById,
} from './staff';
import { addNote } from './staff/add-note';
import { getStaffNotes } from './staff/get-staff-note';
import {
  getSyncAlertConfig,
  simulateSyncFailure,
  testSyncAlert,
  upsertSyncAlertConfig,
} from './sync-alert-config';
import {
  exportUsageReport,
  getOrgSummary,
  getUsageReport,
  getUsageReportByUser,
  postTrackingEvents,
} from './tracking';
import {
  confirmEmailChange,
  deleteMyProfile,
  deleteUserById,
  getContractors,
  getCurrentUser,
  getLoginActivity,
  getOrgAdmins,
  getUserById,
  getUsers,
  inviteUser,
  requestEmailChange,
  restoreUserById,
  sendUserInvite,
  setImpersonationAccess,
  setStaffTemplateAccess,
  updateMyPassword,
  updateMyProfile,
  updateUser,
  updateUserRole,
  uploadUserImage,
} from './users';

export const API = {
  changelog: {
    getChangelog,
  },
  auth: {
    login,
    logout,
    checkSession,
    refreshToken,
    forgotPassword,
    resetPassword,
    createImpersonationLink,
    impersonateExchange,
  },
  users: {
    getCurrentUser,
    getUsers,
    getOrgAdmins,
    setImpersonationAccess,
    setStaffTemplateAccess,
    getLoginActivity,
    getContractors,
    inviteUser,
    uploadUserImage,
    updateMyProfile,
    updateUser,
    updateMyPassword,
    deleteMyProfile,
    deleteUserById,
    getUserById,
    restoreUserById,
    sendUserInvite,
    updateUserRole,
    requestEmailChange,
    confirmEmailChange,
  },
  roles: {
    getRolePermissionMatrix,
    addPermissionToRole,
    removePermissionFromRole,
    getCreatableRoles,
    getRoles,
    updateRole,
    createRole,
  },
  auditLogs: {
    getOrganizationLogs,
  },
  organizations: {
    getOrganizations,
    createOrganizationWithUser,
  },
  integrations: {
    createEmrIntegration,
    getEmrIntegrationProgress,
    getSlingConnectionStatus,
    refetchEmrIntegration,
    saveSlingConnection,
    updateEmrCredentials,
  },
  marketFacilities: {
    getMarketFacilities,
    getMarketFacilityVersionHistory,
  },
  marketFacilitiesForm: {
    createMarketFacilityForm,
    uploadMarketFacilityFormFile,
    searchMarketFacilityForms,
    getMarketFacilityFormById,
    updateMarketFacilityForm,
    publishMarketFacilityForm,
  },
  facilities: {
    assignFacilities,
    getExpenses,
    getFacilities,
    getFacilitiesByManager,
    getOverallOccupancy,
    getOccupancyLosScatter,
    getPayerDistribution,
    getFacilitiesOptions,
    getFacilitiesPricingModels,
    getAlertsPerResident,
    getAverageNumberOfResidents,
    getFacilityTypes,
    unassignFacility,
    getFacilityOccupancy,
    getFacilityExpenses,
    getFacilityRevenue,
    getFacilityLevelsOfCare,
    getFacilityById,
    getFacilityAlertsAndFlagsChart,
    getRevenue,
    updateFacility,
    getOverallOccupancyForecast,
    getRevenueForecast,
    getRevenueAnalysis,
    getRevenueAnalysisForecast,
    getFacilityRevenueForecast,
    getFacilityOccupancyForecast,
    getMargin,
    getMarginById,
    getPayerTypes,
    updatePayerTypes,
    getRevenuePerResident,
    getRevenuePerResidentScoped,
    getRevenuePerBedScoped,
    getAdditionalCharges,
    getFacilitiesPerformance,
    getFacilityLocRatesAndRevenue,
    getFacilityFlags,
    getResolvedAlerts,
    postDashboardInsight,
    streamDashboardInsight,
  },
  amenities: {
    getAllAmenities,
    getAmenitiesById,
    updateAmenities,
  },
  staff: {
    getStaff,
    getStaffById,
    getTotalStaff,
    getStaffingRatio,
    getOverstaffed,
    getUnderstaffed,
    getStaffingRatioTable,
    getTenureDistribution,
    getNewHires,
    getTurnovers,
    getHiresTurnoversChart,
    updateStaffById,
    exportStaff,
    downloadStaffImportTemplate,
    importStaff,
    addNote,
    getStaffNotes,
    getWageHistory,
    deleteNote,
    updateNote,
  },
  alertsSettings: {
    getAlertsSettings,
    updateAlertsSettings,
  },
  floorPlan: {
    getFacilityFloorPlan,
    getBedHistory,
  },
  marketIntel: {
    getMarketIntelReportPdfUrl,
    getAiInsight,
    getAiSummary,
    getCompetitorsLocations,
    getOccupancyKpi,
    getAmrKpi,
    getComparisonInfo,
    getStaffStaffingRatio,
    getStaffAverageTenure,
    getStaffTenureDistribution,
    getStaffTurnoverRate,
    simulateImpact,
    getResidentRetention,
    getFirstYearDischargeRate,
    getTotalAdmissionsKpi,
    getTotalDischargesKpi,
    getMarginKpi,
    getPayerDistributionChart,
    getLengthOfStayDistribution,
    getTotalDischargesByType,
    getTrendsTable,
    getPotentialRevenueLossKpi,
    getAlertsPerPerson,
    getFlagsPerPerson,
    getAvgDaysToResolve,
    getAlertsResolutionEffectiveness,
    getComparisonTable,
    getStaffTrendsTable,
    getAlertsTrendsTable,
    getAlertSeverityDistribution,
    getAlertTopCategories,
  },
  marketIntelSubscriptions: {
    getSubscriptions,
    initializeSubscription,
    updateSubscription,
    getSubscriptionStatus,
    generateReportPdf,
  },
  residents: {
    getResidents,
    getResidentById,
    getResidentNotes,
    addResidentNote,
    updateResidentNote,
    deleteResidentNote,
    getFamilyCommunications,
    addFamilyCommunication,
    updateFamilyCommunication,
    deleteFamilyCommunication,
    downloadFamilyCommunication,
    getAdmissionsKpi,
    getDischargesKpi,
    getLengthOfStayKpi,
    getAdmissionsDischargesChart,
    exportResidents,
    getLengthOfStayDistributionById,
    getDischargesByType,
    getAlertsKpi,
    getPayAcrossCareLevels,
    getResidentAlerts,
  },
  syncAlertConfig: {
    getSyncAlertConfig,
    upsertSyncAlertConfig,
    testSyncAlert,
    simulateSyncFailure,
  },
  tracking: {
    postTrackingEvents,
    getUsageReport,
    getUsageReportByUser,
    getOrgSummary,
    exportUsageReport,
  },
  alerts: {
    getAlerts,
    getAlertConfigOptions,
    getAlertsSummary,
    getAlertById,
    resolveAlert,
    selectAlertOption,
    getAlertAudit,
    getLinkedAlerts,
    getActiveAlertsKpi,
    getCreatedAlertsKpi,
    getCreatedFlagsKpi,
    getAvgDaysToResolveKpi,
    getMonthOverMonthChart,
    exportAlerts,
  },
};
